/**
 * Central metadata definitions used for SEO tags and the sitemap.
 */

export const BASE_URL = 'https://florent-escots.fr';

export const DEFAULT_META = {
  title: 'Tanguy Perroit | Photographe professionnel à Tours',
  description:
    'Studio photo à Tours : portraits, reportages corporate et projets éditoriaux. Découvrez les réalisations et contactez moi pour réservez votre séance.',
  keywords: ['photographe Tours', 'studio photo', 'portrait pro', 'photo institutionnelle'],
  canonical: BASE_URL,
  openGraph: {
    type: 'website',
    title: 'Tanguy Perroit | Photographe professionnel à Tours',
    description:
      'Studio photo à Tours : portraits, reportages corporate et projets éditoriaux. Découvrez les réalisations et réservez votre séance.',
  },
  twitter: {
    card: 'summary_large_image',
    title: 'Tanguy Perroit | Photographe à Tours',
    description: 'Studio photo à Tours : portraits, reportages corporate et projets éditoriaux.'
  },
};

export const SECTION_DEFINITIONS = {
  main: {
    key: 'main',
    title: 'Navigation principale',
    icon: '[NAV]',
    description:
      'Pages essentielles pour découvrir l\'univers et le savoir-faire du studio.',
  },
  services: {
    key: 'services',
    title: 'Services photographiques',
    icon: '[SERV]',
    description: 'Prestations professionnelles pour particuliers et entreprises.',
  },
  projects: {
    key: 'projects',
    title: 'Réalisations et études de cas',
    icon: '[PROJ]',
    description: 'Sélections de projets photographiques et séries thématiques.',
  },
  info: {
    key: 'info',
    title: 'Contact et informations',
    icon: '[INFO]',
    description: 'Informations pratiques, plan du site et formulaires de contact.',
  },
  legal: {
    key: 'legal',
    title: 'Ressources légales',
    icon: '[LEGAL]',
    description: 'Documents officiels et mentions obligatoires.',
  },
};

const STATIC_PAGES = [
  {
    id: 'home',
    path: '/',
    name: 'Accueil',
    section: 'main',
    title: 'Tanguy Perroit | Photographe professionnel à Tours',
    description:
      'Studio photo à Tours : portraits, reportages corporate et projets éditoriaux. Découvrez les réalisations et contactez moi pour réservez votre séance.',
    sitemapDescription:
      'Présentation générale du studio photo, des prestations et des projets phares.',
    keywords: ['photographe Tours', 'studio photo', 'portrait pro', 'photo institutionnelle'],
    changefreq: 'weekly',
    priority: 1.0,
    canonical: BASE_URL,
    openGraph: {
      title: 'Tanguy Perroit | Photographe professionnel à Tours',
      description: 'Studio photo à Tours : portraits, reportages corporate et projets éditoriaux.',
    },
  },
  {
    id: 'about',
    path: '/a-propos',
    name: 'À propos',
    section: 'main',
    title: 'À propos | Tanguy Perroit, photographe à Tours',
    description:
      'Parcours, valeurs et méthode de travail : de la préparation à la livraison, découvrez l\'approche de Tanguy Perroit.',
    sitemapDescription:
      'Rencontrez le photographe et sa vision, de la préparation au rendu final.',
    keywords: ['photographe Tours', 'approche creative', 'parcours photographe', 'Déroulement séance photo Tanguy perroit'],
    changefreq: 'monthly',
    priority: 0.9,
    canonical: `${BASE_URL}/a-propos`,
    openGraph: {
      title: 'À propos | Tanguy Perroit, photographe à Tours',
      description: 'Parcours, valeurs et méthode de travail de Tanguy Perroit, photographe à Tours.',
    },
  },
  {
    id: 'services-portraits',
    path: '/services/portraits',
    name: 'Portraits professionnels',
    section: 'services',
    title: 'Séances portraits professionnels à Tours | Tanguy Perroit',
    description:
      'Portraits en studio ou extérieur, conseils personnalisés et retouches soignées. Réservez votre séance à Tours.',
    sitemapDescription:
      'Détails des séances portraits: déroulement, conseils et exemples réalisés.',
    keywords: ['portrait professionnel Tours', 'seance photo studio', 'portrait corporate'],
    changefreq: 'monthly',
    priority: 0.8,
    canonical: `${BASE_URL}/services/portraits`,
    openGraph: {
      title: 'Séances portraits professionnels à Tours',
      description: 'Portraits en studio ou extérieur avec conseils personnalisés par Tanguy Perroit.',
    },
  },
  {
    id: 'services-entreprise',
    path: '/services/entreprise',
    name: 'Service entreprise',
    section: 'services',
    title: 'Reportages et portraits d\'entreprise | Tanguy Perroit',
    description: 'Portraits collaborateurs, shooting éditorialistes et contenus pour votre communication corporate à Tours et alentours.',
    sitemapDescription: 'Prestations photo corporate et communication visuelle pour les entreprises.',
    keywords: ['photographe entreprise', 'serie corporate', 'portrait collaborateur'],
    changefreq: 'monthly',
    priority: 0.7,
    canonical: `${BASE_URL}/services/entreprise`,
    openGraph: {
      title: 'Reportages et portraits d\'entreprise',
      description: 'Prestations photo pour valoriser votre entreprise avec Tanguy Perroit.',
    },
  },
  {
    id: 'services-evenementiel',
    path: '/services/evenementiel',
    name: 'Service événementiel',
    section: 'services',
    title: 'Photographie événementielle et mariage | Tanguy Perroit',
    description: 'Couverture photo discrète et complète de vos événements et mariages en Centre-Val de Loire.',
    sitemapDescription: 'Photographie événementielle et couverture de mariages et séminaires.',
    keywords: ['photographe evenement', 'mariage Tours', 'photo festival', 'photo date danse'],
    changefreq: 'monthly',
    priority: 0.7,
    canonical: `${BASE_URL}/services/evenementiel`,
    openGraph: {
      title: 'Photographie événementielle et mariage',
      description: 'Couverture photo de vos événements et mariages par Tanguy Perroit.',
    },
  },
  {
    id: 'services-print',
    path: '/services/print',
    name: 'Print',
    section: 'services',
    title: 'Print | Tanguy Perroit',
    description: 'Accompagnement créatif de A à Z pour un rendu unique, fidèle à votre identité de marque ou personnelle.',
    sitemapDescription: 'Services de tirages photo et accompagnement créatif pour vos projets print.',
    keywords: ['Print', 'achat photo', 'tirage photo', 'photo tours', 'tirage photo tours'],
    changefreq: 'monthly',
    priority: 0.7,
    canonical: `${BASE_URL}/services/print`,
    openGraph: {
      title: 'Services Print | Tanguy Perroit',
      description: 'Tirages photo et accompagnement créatif pour un rendu unique.',
    },
  },
  {
    id: 'realisations',
    path: '/realisations',
    name: 'Réalisations',
    section: 'projects',
    title: 'Réalisations photographiques | Tanguy Perroit',
    description:
      'Galerie de projets classés par thèmes : mariages, portraits, corporate et voyages. Parcourez le portfolio du studio.',
    sitemapDescription:
      'Vue d\'ensemble des projets photographiques livrés aux clients du studio.',
    keywords: ['portfolio photographe Tours', 'projets photo', 'reportage photo'],
    changefreq: 'weekly',
    priority: 0.8,
    canonical: `${BASE_URL}/realisations`,
    openGraph: {
      title: 'Réalisations photographiques | Tanguy Perroit',
      description: 'Sélection de projets photo : mariages, portraits, corporate et voyages.',
    },
  },
  {
    id: 'contact',
    path: '/contact',
    name: 'Contact',
    section: 'info',
    title: 'Contact | Discutons de votre projet photo',
    description:
      'Coordonnées, formulaire et disponibilités pour échanger avec Tanguy Perroit et construire votre projet photo à Tours.',
    sitemapDescription:
      'Formulaire de contact, disponibilités et informations pratiques.',
    keywords: ['contact photographe Tours', 'devis photo', 'rendez-vous studio'],
    changefreq: 'monthly',
    priority: 0.7,
    canonical: `${BASE_URL}/contact`,
    openGraph: {
      title: 'Contact | Discutons de votre projet photo',
      description: 'Contactez Tanguy Perroit pour un devis ou une séance photo à Tours.',
    },
  },
  {
    id: 'sitemap',
    path: '/sitemap',
    name: 'Plan du site',
    section: 'info',
    title: 'Plan du site | Tanguy Perroit',
    description:
      'Plan du site pour retrouver rapidement prestations, réalisations, contact et informations légales du studio.',
    sitemapDescription: 'Navigation complète vers toutes les pages du site.',
    keywords: ['plan du site', 'sitemap photographe', 'navigation site'],
    changefreq: 'monthly',
    priority: 0.4,
    canonical: `${BASE_URL}/sitemap`,
    openGraph: {
      title: 'Plan du site Tanguy Perroit',
      description: 'Navigation complète du site Tanguy Perroit.',
    },
  },
  {
    id: 'mentions-legales',
    path: '/mentions-legales',
    name: 'Mentions légales',
    section: 'legal',
    title: 'Mentions légales | Tanguy Perroit',
    description:
      'Mentions légales, politique de confidentialité et informations réglementaires du site.',
    sitemapDescription: 'Mentions légales et informations obligatoires.',
    keywords: ['mentions legales', 'photographe Tours', 'politique de confidentialite'],
    changefreq: 'yearly',
    priority: 0.3,
    canonical: `${BASE_URL}/mentions-legales`,
    openGraph: {
      title: 'Mentions légales | Tanguy Perroit',
      description: 'Mentions légales et politique de données du site Tanguy Perroit.',
    },
  },
  {
    id: 'admin',
    path: '/admin',
    name: 'Espace administrateur',
    section: 'info',
    title: 'Espace administrateur | Tanguy Perroit',
    description: 'Interface interne réservée à la gestion des projets et contenus du site Tanguy Perroit.',
    sitemapDescription: 'Back office pour administrer les projets.',
    keywords: ['administration', 'gestion portfolio', 'interface interne'],
    changefreq: 'monthly',
    priority: 0.1,
    canonical: `${BASE_URL}/admin`,
    includeInSitemap: false,
  },
];

const STATIC_PAGE_MAP = new Map(STATIC_PAGES.map((page) => [page.path, page]));

const PROJECT_DETAIL_FALLBACK = {
  id: 'project-detail',
  path: '/realisation/:slug',
  name: 'Fiche projet',
  section: 'projects',
  title: 'Projet photographique | Tanguy Perroit',
  description: 'Étude de cas détaillée d\'un projet photographié réalisé par Tanguy Perroit.',
  sitemapDescription: 'Fiche descriptive d\'une réalisation photographique.',
  keywords: ['projet photo', 'realisation', 'photographe Tours'],
  changefreq: 'monthly',
  priority: 0.6,
  includeInSitemap: false,
  canonical: `${BASE_URL}/realisations`,
};

export const getStaticPages = () => STATIC_PAGES;

export const getPageInfoByPath = (path) => {
  if (!path) return null;
  if (STATIC_PAGE_MAP.has(path)) {
    return STATIC_PAGE_MAP.get(path);
  }
  if (path.startsWith('/realisation/')) {
    return PROJECT_DETAIL_FALLBACK;
  }
  return null;
};

const ensureMetaTag = (attribute, value) => {
  if (typeof document === 'undefined') return null;
  const selector = `meta[${attribute}="${value}"]`;
  const existing = document.querySelector(selector);
  if (existing) return existing;
  const metaTag = document.createElement('meta');
  metaTag.setAttribute(attribute, value);
  document.head.appendChild(metaTag);
  return metaTag;
};

const ensureLinkTag = (rel) => {
  if (typeof document === 'undefined') return null;
  const selector = `link[rel="${rel}"]`;
  const existing = document.querySelector(selector);
  if (existing) return existing;
  const link = document.createElement('link');
  link.setAttribute('rel', rel);
  document.head.appendChild(link);
  return link;
};

export const updatePageMetadata = (info) => {
  if (typeof document === 'undefined') return;
  const meta = info ? { ...DEFAULT_META, ...info } : { ...DEFAULT_META };

  document.title = meta.title || DEFAULT_META.title;

  const description = meta.description || DEFAULT_META.description;
  ensureMetaTag('name', 'description')?.setAttribute('content', description);

  const keywordList = Array.isArray(meta.keywords)
    ? meta.keywords
    : typeof meta.keywords === 'string'
      ? meta.keywords.split(',').map((word) => word.trim()).filter(Boolean)
      : DEFAULT_META.keywords;
  ensureMetaTag('name', 'keywords')?.setAttribute('content', keywordList.join(', '));

  const robotsValue = meta.robots || DEFAULT_META.robots;
  if (robotsValue) {
    ensureMetaTag('name', 'robots')?.setAttribute('content', robotsValue);
  }

  const canonicalHref = meta.canonical || DEFAULT_META.canonical;
  if (canonicalHref) {
    ensureLinkTag('canonical')?.setAttribute('href', canonicalHref);
  }

  const og = { ...DEFAULT_META.openGraph, ...(meta.openGraph || {}) };
  ensureMetaTag('property', 'og:title')?.setAttribute('content', og.title || meta.title || DEFAULT_META.title);
  ensureMetaTag('property', 'og:description')?.setAttribute('content', og.description || description);
  ensureMetaTag('property', 'og:type')?.setAttribute('content', og.type || 'website');
  if (og.url || canonicalHref) {
    ensureMetaTag('property', 'og:url')?.setAttribute('content', og.url || canonicalHref);
  }
  if (og.image) {
    ensureMetaTag('property', 'og:image')?.setAttribute('content', og.image);
  }

  const twitter = { ...DEFAULT_META.twitter, ...(meta.twitter || {}) };
  ensureMetaTag('name', 'twitter:card')?.setAttribute('content', twitter.card || 'summary');
  ensureMetaTag('name', 'twitter:title')?.setAttribute('content', twitter.title || meta.title || DEFAULT_META.title);
  ensureMetaTag('name', 'twitter:description')?.setAttribute('content', twitter.description || description);
  if (twitter.image || og.image) {
    ensureMetaTag('name', 'twitter:image')?.setAttribute('content', twitter.image || og.image);
  }
};

export const getSectionDefinition = (key) => SECTION_DEFINITIONS[key] || null;

export const getProjectDetailFallback = () => PROJECT_DETAIL_FALLBACK;

export default STATIC_PAGES;